class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // Use event delegation for overlay and close button clicks
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay') {
        this.close();
        return;
      }
      
      const closeButton = evt.target.closest('.drawer__close');
      if (closeButton && this.contains(closeButton)) {
        evt.preventDefault();
        this.close();
        return;
      }
    });
    
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    
    // SAFARI FIX: Force complete re-render by manipulating display
    // This is the same fix that worked when you toggled it in devtools
    const originalDisplay = window.getComputedStyle(this).display;
    this.style.display = 'none';
    // Force browser to acknowledge the display change
    this.offsetHeight;
    // Restore display after a frame to ensure Safari processes it
    requestAnimationFrame(() => {
      this.style.display = '';
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const { skipOpen = false } = options;
    
    // SAFARI FIX: Clear any stuck states before updating
    const wasActive = this.classList.contains('active');
    
    // Force clean the display state if drawer is stuck open
    if (!wasActive && window.getComputedStyle(this).visibility === 'visible') {
      // Safari has gotten into a bad state - force reset
      this.style.display = 'none';
      this.offsetHeight;
      this.style.display = '';
    }
    
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
        HTMLUpdateUtility.setInnerHTML(
        sectionElement,
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)
      );
    });

    // Notify components that totals changed after a full drawer re-render
    try {
      const shippingEl = document.getElementById('drawer-shipping-cost');
      const cartTotalFromDom = shippingEl ? parseInt(shippingEl.getAttribute('data-cart-total') || '0', 10) : null;
      const cartTotalFromState = typeof parsedState?.total_price === 'number' ? parsedState.total_price : null;

      const total = cartTotalFromState ?? cartTotalFromDom ?? 0;

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cartTotal: total }
      }));
    } catch (e) {
      // no-op; don't block rendering if anything goes wrong
    }

    if (!skipOpen && !wasActive) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);